<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { onClickOutside } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';
import TemplateEditor from './editor/TemplateEditor.vue';
import VariablePicker from './editor/VariablePicker.vue';
import { injectPagination } from './editor/previewPagination';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();

const templateId = computed(() => route.params.templateId);
const isEditing = computed(() => Boolean(templateId.value));

const EMPTY_DOC = {
  type: 'doc',
  content: [{ type: 'paragraph', content: [] }],
};

const name = ref('');
const body = ref(EMPTY_DOC);
const version = ref(1);
const pageConfig = ref({
  paper: 'letter',
  orientation: 'portrait',
  show_page_numbers: false,
});

const saving = ref(false);
const editorRef = ref(null);

const showSettings = ref(false);
const settingsRef = ref(null);
onClickOutside(settingsRef, () => {
  showSettings.value = false;
});

const showPreview = ref(false);
const previewHtml = ref('');
const previewLoading = ref(false);

onMounted(async () => {
  if (!isEditing.value) return;
  const template = await store.dispatch(
    'documentTemplates/show',
    templateId.value
  );
  name.value = template.name;
  body.value = template.body || EMPTY_DOC;
  version.value = template.version || 1;
  pageConfig.value = { ...pageConfig.value, ...(template.page_config || {}) };
});

function backToList() {
  router.push({
    name: 'documents_settings_list',
    params: { accountId: accountId.value },
  });
}

function insertVariable(key, label) {
  editorRef.value?.insertVariable(key, label);
}

function buildPayload() {
  return {
    document_template: {
      name: name.value,
      body: body.value,
      page_config: pageConfig.value,
    },
  };
}

async function save() {
  if (!name.value.trim()) {
    useAlert(t('DOCUMENT_TEMPLATES.EDITOR.NAME_REQUIRED'));
    return;
  }
  saving.value = true;
  try {
    if (isEditing.value) {
      const updated = await store.dispatch('documentTemplates/update', {
        id: templateId.value,
        ...buildPayload(),
      });
      version.value = updated.version || version.value;
      useAlert(t('DOCUMENT_TEMPLATES.API.UPDATE_SUCCESS'));
    } else {
      const created = await store.dispatch(
        'documentTemplates/create',
        buildPayload()
      );
      useAlert(t('DOCUMENT_TEMPLATES.API.CREATE_SUCCESS'));
      router.push({
        name: 'documents_settings_edit',
        params: { accountId: accountId.value, templateId: String(created.id) },
      });
    }
  } catch {
    useAlert(t('DOCUMENT_TEMPLATES.API.ERROR'));
  } finally {
    saving.value = false;
  }
}

async function openPreview() {
  showPreview.value = true;
  previewLoading.value = true;
  previewHtml.value = '';
  try {
    const html = await store.dispatch('documentTemplates/preview', {
      id: templateId.value,
      // Send the editor's live state so the preview matches what's on screen —
      // including a just-changed paper size (Carta/Oficio) before saving.
      body: editorRef.value?.getJSON() || body.value,
      pageConfig: pageConfig.value,
    });
    // Inject the paged.js polyfill (served from public/) so the print-ready HTML
    // renders as real page boxes in the iframe, matching the PDF.
    previewHtml.value = injectPagination(html);
  } catch {
    useAlert(t('DOCUMENT_TEMPLATES.API.ERROR'));
    showPreview.value = false;
  } finally {
    previewLoading.value = false;
  }
}
</script>

<template>
  <div class="flex flex-col h-full bg-n-background">
    <!-- Top bar -->
    <header
      class="flex justify-between items-center px-4 h-14 border-b border-n-weak bg-n-solid-1"
    >
      <div class="flex gap-3 items-center min-w-0">
        <Button
          variant="ghost"
          color="slate"
          icon="i-lucide-arrow-left"
          size="sm"
          @click="backToList"
        />
        <div class="min-w-0">
          <h1 class="text-base font-semibold truncate text-n-slate-12">
            {{
              isEditing
                ? name || t('DOCUMENT_TEMPLATES.EDITOR.EDIT_TITLE')
                : t('DOCUMENT_TEMPLATES.EDITOR.NEW_TITLE')
            }}
          </h1>
        </div>
        <Badge v-if="isEditing" color="gray" size="small">v{{ version }}</Badge>
      </div>
      <div class="flex gap-2 items-center">
        <Button
          v-if="isEditing"
          variant="outline"
          color="slate"
          icon="i-lucide-eye"
          :label="t('DOCUMENT_TEMPLATES.PREVIEW.BUTTON')"
          size="sm"
          @click="openPreview"
        />
        <Button
          icon="i-lucide-save"
          :label="t('DOCUMENT_TEMPLATES.FORM.SAVE')"
          :is-loading="saving"
          size="sm"
          @click="save"
        />
      </div>
    </header>

    <div class="flex overflow-hidden flex-1">
      <!-- Editor column -->
      <div class="flex flex-col flex-1 min-w-0">
        <!-- Info bar -->
        <div
          class="flex flex-wrap gap-3 items-center px-4 py-2 border-b border-n-weak bg-n-solid-2"
        >
          <input
            v-model="name"
            type="text"
            :placeholder="t('DOCUMENT_TEMPLATES.FORM.NAME_PLACEHOLDER')"
            class="flex-1 px-3 py-1.5 min-w-48 text-sm font-medium rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          />

          <div class="flex gap-2 items-center">
            <span class="i-lucide-file-text size-4 text-n-slate-10" />
            <select
              v-model="pageConfig.paper"
              class="px-2 py-1.5 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
            >
              <option value="letter">Carta</option>
              <option value="legal">Oficio</option>
              <option value="a4">A4</option>
            </select>
            <select
              v-model="pageConfig.orientation"
              class="px-2 py-1.5 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
            >
              <option value="portrait">
                {{ t('DOCUMENT_TEMPLATES.FORM.PORTRAIT') }}
              </option>
              <option value="landscape">
                {{ t('DOCUMENT_TEMPLATES.FORM.LANDSCAPE') }}
              </option>
            </select>

            <div ref="settingsRef" class="relative">
              <Button
                variant="outline"
                color="slate"
                icon="i-lucide-settings-2"
                size="sm"
                @click="showSettings = !showSettings"
              />
              <div
                v-if="showSettings"
                class="absolute right-0 top-full z-50 p-3 mt-1 w-56 rounded-lg border shadow-lg border-n-weak bg-n-solid-1"
              >
                <label class="flex gap-2 items-center text-sm text-n-slate-12">
                  <input
                    v-model="pageConfig.show_page_numbers"
                    type="checkbox"
                    class="accent-n-brand"
                  />
                  {{ t('DOCUMENT_TEMPLATES.FORM.PAGE_NUMBERS') }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Canvas -->
        <TemplateEditor
          ref="editorRef"
          v-model="body"
          :placeholder="t('DOCUMENT_TEMPLATES.EDITOR.PLACEHOLDER')"
          :paper-size="pageConfig.paper"
          :orientation="pageConfig.orientation"
        />
      </div>

      <!-- Variables sidebar -->
      <aside
        class="flex flex-col w-80 border-l border-n-weak bg-n-solid-1 shrink-0"
      >
        <div class="flex gap-2 items-center px-4 h-11 border-b border-n-weak">
          <span class="i-lucide-braces size-4 text-n-slate-11" />
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('DOCUMENT_TEMPLATES.EDITOR.VARIABLES.TITLE') }}
          </h2>
        </div>
        <VariablePicker class="flex-1 min-h-0" @insert="insertVariable" />
      </aside>
    </div>

    <!-- Preview modal -->
    <div
      v-if="showPreview"
      class="flex fixed inset-0 z-50 justify-center items-center p-6 bg-black/50"
      @click.self="showPreview = false"
    >
      <div
        class="flex flex-col w-full max-w-4xl rounded-xl shadow-xl bg-n-solid-1 max-h-[90vh]"
      >
        <div
          class="flex justify-between items-center px-4 h-12 border-b border-n-weak"
        >
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ name || t('DOCUMENT_TEMPLATES.PREVIEW.TITLE') }}
          </h3>
          <Button
            variant="ghost"
            color="slate"
            icon="i-lucide-x"
            size="sm"
            @click="showPreview = false"
          />
        </div>
        <div class="overflow-auto flex-1 p-4 bg-n-slate-2">
          <div
            v-if="previewLoading"
            class="flex justify-center items-center py-12"
          >
            <span
              class="animate-spin i-lucide-loader-2 size-6 text-n-slate-10"
            />
          </div>
          <iframe
            v-else
            :srcdoc="previewHtml"
            class="w-full h-[70vh] bg-white rounded-md border border-n-weak"
            title="preview"
          />
        </div>
      </div>
    </div>
  </div>
</template>
